import React from 'react';
import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { usePermissions, PERMISSIONS } from '../../contexts/PermissionContext';
import LanguageSwitcher from '../common/LanguageSwitcher';
import './Layout.css';

const Layout: React.FC = () => {
  const { user, logout } = useAuth();
  const { hasPermission, hasAnyPermission } = usePermissions();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  return (
    <div className="layout">
      <nav className="sidebar">
        <div className="sidebar-header">
          <h2>HRMS</h2>
          <LanguageSwitcher />
        </div>
        <ul className="nav-menu">
          {/* Leave Balance - available to all */}
          <li>
            <NavLink to="/leave-requests/balance" className={({ isActive }) => isActive ? 'active' : ''}>
              {t('leaveRequests.balance')}
            </NavLink>
          </li>
          <li>
            <NavLink to="/leave-requests/me" className={({ isActive }) => isActive ? 'active' : ''}>
              {t('leaveRequests.myRequests')}
            </NavLink>
          </li>
          <li>
            <NavLink to="/leave-requests/new" className={({ isActive }) => isActive ? 'active' : ''}>
              {t('leaveRequests.newRequest')}
            </NavLink>
          </li>
          
          {/* Approvals - requires approve permission */}
          {hasPermission(PERMISSIONS.LEAVE_REQUESTS_APPROVE) && (
            <li>
              <NavLink to="/leave-requests/approvals" className={({ isActive }) => isActive ? 'active' : ''}>
                {t('leaveRequests.approvals')}
              </NavLink>
            </li>
          )}
          
          {/* Reports - requires any report permission */}
          {hasAnyPermission([
            PERMISSIONS.REPORTS_HEADCOUNT,
            PERMISSIONS.REPORTS_LEAVE_SUMMARY,
            PERMISSIONS.REPORTS_LATE_SUMMARY,
            PERMISSIONS.REPORTS_APPROVAL_SLA,
          ]) && (
            <li>
              <NavLink to="/reports" className={({ isActive }) => isActive ? 'active' : ''}>
                {t('reports.title')}
              </NavLink>
            </li>
          )}
          
          {/* Management Section - show if has any management permissions */}
          {hasAnyPermission([PERMISSIONS.DEPARTMENTS_VIEW, PERMISSIONS.EMPLOYEES_VIEW, PERMISSIONS.USERS_VIEW]) && (
            <>
              <li className="nav-divider"></li>
              <li className="nav-section-title">{t('common.management', 'Management')}</li>
              
              {hasPermission(PERMISSIONS.DEPARTMENTS_VIEW) && (
                <li>
                  <NavLink to="/departments" className={({ isActive }) => isActive ? 'active' : ''}>
                    {t('departments.title')}
                  </NavLink>
                </li>
              )}
              
              {hasPermission(PERMISSIONS.EMPLOYEES_VIEW) && (
                <li>
                  <NavLink to="/employees" className={({ isActive }) => isActive ? 'active' : ''}>
                    {t('employees.title')}
                  </NavLink>
                </li>
              )}
              
              {hasPermission(PERMISSIONS.USERS_VIEW) && (
                <li>
                  <NavLink to="/users" className={({ isActive }) => isActive ? 'active' : ''}>
                    {t('users.title')}
                  </NavLink>
                </li>
              )}
            </>
          )}
          
          {/* Settings Section - show if has any settings permissions */}
          {hasAnyPermission([
            PERMISSIONS.GROUPS_VIEW,
            PERMISSIONS.SETTINGS_VIEW,
            PERMISSIONS.AUDIT_VIEW,
          ]) && (
            <>
              <li className="nav-divider"></li>
              <li className="nav-section-title">{t('common.settings', 'Settings')}</li>
              
              {hasPermission(PERMISSIONS.GROUPS_VIEW) && (
                <li>
                  <NavLink to="/permission-groups" className={({ isActive }) => isActive ? 'active' : ''}>
                    {t('permissionGroups.title', 'Permission Groups')}
                  </NavLink>
                </li>
              )}
              
              {hasPermission(PERMISSIONS.SETTINGS_VIEW) && (
                <li>
                  <NavLink to="/settings" className={({ isActive }) => isActive ? 'active' : ''}>
                    {t('settings.system', 'System Settings')}
                  </NavLink>
                </li>
              )}
              
              {hasPermission(PERMISSIONS.AUDIT_VIEW) && (
                <li>
                  <NavLink to="/settings/audit-logs" className={({ isActive }) => isActive ? 'active' : ''}>
                    {t('settings.auditLogs', 'Audit Logs')}
                  </NavLink>
                </li>
              )}
            </>
          )}
        </ul>
        <div className="sidebar-footer">
          <div className="user-info">
            <p className="user-name">{user?.email}</p>
            <p className="user-role">{user?.role ? t(`roles.${user.role.toLowerCase()}`) : ''}</p>
          </div>
          <button onClick={handleLogout} className="logout-button">
            {t('common.logout', 'Logout')}
          </button>
        </div>
      </nav>
      <main className="main-content">
        <Outlet />
      </main>
    </div>
  );
};

export default Layout;